import React from 'react';
import { FiShield, FiTrendingUp, FiCheckCircle, FiClock, FiFileText,  FiUsers, FiAward, FiBarChart2 } from 'react-icons/fi';

const UGCAICTE = () => {
  const complianceAreas = [
    {
      icon: <FiFileText className="w-6 h-6" />,
      title: 'Program and Course Approval',
      description: 'UGC and AICTE require detailed records of all programs and courses offered. Institutions must maintain documentation of approvals, curriculum updates, and compliance with regulatory standards. Without a centralized system, tracking these records becomes a nightmare.',
      stat: '85% of institutions struggle with program approval documentation',
      solution: 'Truyon maintains complete program approval records, curriculum versions, and compliance documentation.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Faculty Qualifications and Appointments',
      description: 'UGC and AICTE have strict guidelines on faculty qualifications, experience, and appointment processes. Institutions must maintain detailed records of all faculty members, their qualifications, and their appointments.',
      stat: '75% of institutions face faculty documentation challenges',
      solution: 'Truyon tracks faculty qualifications, experience, and appointment records automatically.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Student Enrollment and Academic Records',
      description: 'Compliance requires accurate student enrollment data, academic records, and progression tracking. Institutions must maintain comprehensive records of every student from admission to graduation.',
      stat: '90% of institutions report enrollment tracking as a compliance priority',
      solution: 'Truyon manages complete student lifecycle records for seamless compliance reporting.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Infrastructure and Facilities Documentation',
      description: 'UGC and AICTE require documentation of infrastructure, facilities, and resources. Institutions must maintain records of classrooms, laboratories, library resources, and other facilities.',
      stat: '70% of institutions find infrastructure documentation challenging',
      solution: 'Truyon tracks infrastructure details and generates compliance-ready facility reports.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiAward className="w-6 h-6" />,
      title: 'Research and Innovation Tracking',
      description: 'For universities and research institutions, UGC expects detailed records of research output, publications, patents, and innovation activities. These records are essential for accreditation and funding.',
      stat: '65% of universities struggle with research documentation',
      solution: 'Truyon tracks research output, publications, patents, and innovation metrics.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Institutional Data and Annual Reports',
      description: 'UGC and AICTE require comprehensive annual reports with institutional data. These reports must be accurate, complete, and submitted on time. Manual compilation is time-consuming and error-prone.',
      stat: '80% of institutions spend weeks preparing annual compliance reports',
      solution: 'Truyon generates UGC and AICTE compliance reports automatically.',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Audit-Ready Always',
      description: 'All compliance records are maintained and accessible instantly.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Error-Free Documentation',
      description: 'Automated records eliminate manual errors and inconsistencies.',
      icon: <FiCheckCircle className="w-5 h-5" />
    },
    {
      title: 'Centralized Records',
      description: 'All compliance documentation in one place. No more scattered files.',
      icon: <FiFileText className="w-5 h-5" />
    },
    {
      title: 'Time Savings',
      description: 'Reduce report preparation time from weeks to minutes.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Accreditation Support',
      description: 'Data ready for NAAC, NIRF, NBA, and other accreditation bodies.',
      icon: <FiAward className="w-5 h-5" />
    },
    {
      title: 'Regulatory Peace of Mind',
      description: 'Always compliant with UGC, AICTE, and other regulatory requirements.',
      icon: <FiTrendingUp className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        For degree colleges and universities in India, compliance with UGC and AICTE regulations is not optional. It's mandatory. Institutions must maintain detailed records of programs, faculty, students, infrastructure, and much more. And they must be ready for audits at any time.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The problem is that <strong className="text-slate-900">compliance documentation is a massive administrative burden.</strong> Records are scattered across departments. Files are outdated. Reports take weeks to compile. And when an audit comes, everyone scrambles to find the right documents.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how educational ERP helps degree colleges and universities stay UGC and AICTE compliant—and always audit-ready.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">85%</div>
          <div className="text-sm text-slate-500">Struggle with program approval documentation</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">90%</div>
          <div className="text-sm text-slate-500">Report enrollment tracking as a compliance priority</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">80%</div>
          <div className="text-sm text-slate-500">Spend weeks preparing annual compliance reports</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">UGC and AICTE Compliance Areas</h2>

      {/* Compliance Areas */}
      {complianceAreas.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Helps</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Why UGC/AICTE Compliance Matters</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Manual vs. ERP-Supported Compliance</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Manual Compliance Management</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Scattered documentation across departments</span></li>
              <li className="flex items-start gap-2">• <span>Outdated records and missing documents</span></li>
              <li className="flex items-start gap-2">• <span>Report preparation takes weeks</span></li>
              <li className="flex items-start gap-2">• <span>Audit anxiety and last-minute scrambling</span></li>
              <li className="flex items-start gap-2">• <span>High risk of non-compliance</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">ERP-Supported Compliance</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Centralized compliance documentation</span></li>
              <li className="flex items-start gap-2">• <span>Always current and accurate records</span></li>
              <li className="flex items-start gap-2">• <span>Reports generated in seconds</span></li>
              <li className="flex items-start gap-2">• <span>Audit-ready always, no stress</span></li>
              <li className="flex items-start gap-2">• <span>Complete compliance assurance</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Audit-Ready Advantage */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">What Audit-Ready Looks Like</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Complete Documentation</h4>
            <p className="text-xs text-slate-600">Every program, faculty member, and student is fully documented</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">🔄</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Updates</h4>
            <p className="text-xs text-slate-600">Records are updated automatically as changes happen</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Instant Access</h4>
            <p className="text-xs text-slate-600">Any document available instantly for inspection</p>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for UGC/AICTE Compliance</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Program Records</h4>
            <p className="text-xs text-slate-600">Complete program approval documentation</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">👨‍🏫</div>
            <h4 className="font-bold text-slate-900 text-sm">Faculty Records</h4>
            <p className="text-xs text-slate-600">Qualification and appointment tracking</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎓</div>
            <h4 className="font-bold text-slate-900 text-sm">Student Records</h4>
            <p className="text-xs text-slate-600">Complete student lifecycle management</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Infrastructure</h4>
            <p className="text-xs text-slate-600">Facility and resource documentation</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔬</div>
            <h4 className="font-bold text-slate-900 text-sm">Research</h4>
            <p className="text-xs text-slate-600">Publications and innovation tracking</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">Compliance Reports</h4>
            <p className="text-xs text-slate-600">Automated UGC and AICTE reports</p>
          </div>
        </div>
      </div>

      {/* The Consequences */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Cost of Non-Compliance</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Failing to maintain UGC and AICTE compliance has serious consequences. Institutions can lose their approvals, face penalties, or damage their reputation. Students may face uncertainty about their degrees. Faculty may lose confidence in the institution.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Compliance isn't just about avoiding penalties—it's about maintaining trust and credibility.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Stay Audit-Ready with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          UGC and AICTE compliance doesn't have to be stressful. With the right systems, institutions can maintain complete records, generate reports instantly, and always be ready for audits.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon provides everything you need for UGC and AICTE compliance. Program records, faculty documentation, student records, infrastructure tracking, research management, and automated compliance reports—all in one platform.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for UGC/AICTE compliance.</strong> Always audit-ready. Always compliant.
        </p>
      </div>
    </>
  );
};

export default UGCAICTE;